import {
	Box,
	Checkbox,
	InputAdornment,
	OutlinedInput,
	outlinedInputClasses,
	Popover,
} from '@mui/material'
import { ReactComponent as CheckBox } from 'assets/icons/checkBox.svg'
import CheckBoxActive from 'assets/icons/checkBoxActive'
import { ReactComponent as FilterIcon } from 'assets/icons/filter-icon.svg'
import { ReactComponent as SearchIcon } from 'assets/search.svg'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { IFiltersLamenessTable, LamenessStatus } from 'models'
import CattlesApiService from 'services/cattlesApiServices'
import { defaultInitialState } from 'store/lamenessFilterSlice'
import { COLORS } from 'styles/colors'
import AutocompleteInput from 'components/shared/textFields/AutocompleteInput'
import { ButtonUI } from 'ui/buttons/ButtonUI'
import CustomButton from 'ui/buttons/CustomButton'
import DateRangePicker from 'ui/DatePickerInput/DateRangePicker'
import { DateRestriction } from 'ui/DatePickerInput/types'
import SelectSmall from 'ui/menu/SelectForm'
import { TypographyHeader14 } from 'ui/typography/TypographyHeader14'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographyPrimary16Bold } from 'ui/typography/TypographyPrimary16Bold'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { ReactComponent as CrossCloseLogo } from '../../../../assets/cross_close_logo.svg'

type RangeValue = {
	start: number | ''
	end: number | ''
}

export type CattlesFilterStats = {
	age: RangeValue
	lactation: RangeValue
	lastMilking: RangeValue
	fatness: RangeValue
	limpnessDays: RangeValue
}

type FilterSectionKey =
	| 'cowAge'
	| 'lactation'
	| 'lastMilking'
	| 'groups'
	| 'fatness'
	| 'lameness'

interface CattlesFilterContentProps {
	activeFilters: IFiltersLamenessTable
	setActiveFilters: React.Dispatch<React.SetStateAction<IFiltersLamenessTable>>
	isFilterActive: boolean
	returnInitialProps: () => void
	availableStats: CattlesFilterStats
	groupId?: number
	tagIdSearch?: string
	identified?: boolean
	showLastMilking: boolean
	showFatness: boolean
	showLameness: boolean
	availableGroups: { id: number; title: string }[]
	resetInline?: boolean
}

const icon = <CheckBox />
const checkedIcon = <CheckBoxActive />

const lamenessStatuses = [
	{ key: LamenessStatus.One, title: '1 балл', id: 1 },
	{ key: LamenessStatus.Two, title: '2 балла', id: 2 },
	{ key: LamenessStatus.Three, title: '3 балла', id: 3 },
	{ key: LamenessStatus.Four, title: '4 балла', id: 4 },
	{ key: LamenessStatus.Five, title: '5 баллов', id: 5 },
]

const fatnessOptions = [
	{ type: 1, value: '1' },
	{ type: 1.25, value: '1,25' },
	{ type: 1.5, value: '1,5' },
	{ type: 1.75, value: '1,75' },
	{ type: 2, value: '2' },
	{ type: 2.25, value: '2,25' },
	{ type: 2.5, value: '2,5' },
	{ type: 2.75, value: '2,75' },
	{ type: 3, value: '3' },
	{ type: 3.25, value: '3,25' },
	{ type: 3.5, value: '3,5' },
	{ type: 3.75, value: '3,75' },
	{ type: 4, value: '4' },
	{ type: 4.25, value: '4,25' },
	{ type: 4.5, value: '4,5' },
	{ type: 4.75, value: '4,75' },
	{ type: 5, value: '5' },
]

const hasRangeValue = (range?: {
	startOf: number | string
	endOf: number | string
}) => Boolean(range && (range.startOf !== '' || range.endOf !== ''))

const hasStatsValue = (range: RangeValue) =>
	range.start !== '' && range.end !== ''

const isEmptyValue = (value: number | string | null | undefined) =>
	value === '' || value == null

const isSeededRange = (
	range: { startOf: number | string; endOf: number | string },
	stats: RangeValue,
) =>
	hasStatsValue(stats) &&
	!isEmptyValue(range.startOf) &&
	!isEmptyValue(range.endOf) &&
	Number(range.startOf) === Number(stats.start) &&
	Number(range.endOf) === Number(stats.end)

export type SeededRangeKey = 'cowAge' | 'lactation' | 'lameness' | 'fatness'

export const getTouchedRanges = (
	filters: IFiltersLamenessTable,
): Set<SeededRangeKey> =>
	new Set(
		(['cowAge', 'lactation', 'lameness', 'fatness'] as const).filter(key =>
			hasRangeValue(filters[key]),
		),
	)

export const stripSeededRanges = (
	filters: IFiltersLamenessTable,
	stats: CattlesFilterStats,
	defaults: IFiltersLamenessTable,
	touched: ReadonlySet<SeededRangeKey>,
): IFiltersLamenessTable => {
	const resetRange = <T extends { startOf: unknown; endOf: unknown }>(
		range: T,
		defaultRange: T,
	): T => ({
		...range,
		startOf: defaultRange.startOf,
		endOf: defaultRange.endOf,
	})

	const isUntouchedSeed = (
		key: SeededRangeKey,
		range: { startOf: number | string; endOf: number | string },
		rangeStats: RangeValue,
	) => !touched.has(key) && isSeededRange(range, rangeStats)

	return {
		...filters,
		cowAge: isUntouchedSeed('cowAge', filters.cowAge, stats.age)
			? resetRange(filters.cowAge, defaults.cowAge)
			: filters.cowAge,
		lactation: isUntouchedSeed('lactation', filters.lactation, stats.lactation)
			? resetRange(filters.lactation, defaults.lactation)
			: filters.lactation,
		lameness: isUntouchedSeed('lameness', filters.lameness, stats.limpnessDays)
			? resetRange(filters.lameness, defaults.lameness)
			: filters.lameness,
		fatness: isUntouchedSeed('fatness', filters.fatness, stats.fatness)
			? resetRange(filters.fatness, defaults.fatness)
			: filters.fatness,
	}
}

const getSectionActive = (
	key: FilterSectionKey,
	filters: IFiltersLamenessTable,
) => {
	switch (key) {
		case 'cowAge':
			return hasRangeValue(filters.cowAge)
		case 'lactation':
			return hasRangeValue(filters.lactation)
		case 'lastMilking':
			return hasRangeValue(filters.lastMilking)
		case 'groups':
			return filters.groups.ids.length > 0
		case 'fatness':
			return hasRangeValue(filters.fatness)
		case 'lameness':
			return (
				filters.lameness.statuses.length > 0 || hasRangeValue(filters.lameness)
			)
		default:
			return false
	}
}

const rangeInputSx = {
	width: '120px',
	borderRadius: '8px',
	justifyContent: 'center',
	'& .MuiOutlinedInput-notchedOutline': {
		borderRadius: '8px',
		borderColor: COLORS.inputBorder,
	},
	'& .MuiInputAdornment-root': {
		marginRight: '4px',
	},
	'& input': {
		textAlign: 'left',
		flex: '0 1 auto',
		width: '42px',
		paddingLeft: 0,
	},
	[`&:hover .${outlinedInputClasses.notchedOutline}`]: {
		borderColor: COLORS.main,
	},
	[`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
		border: `1px solid ${COLORS.main}`,
	},
}

const normalizeNumberInput = (value: string, allowDecimal: boolean) => {
	const normalized = value.replace(',', '.')
	const pattern = allowDecimal ? /[^0-9.]/g : /[^0-9]/g
	const sanitized = normalized.replace(pattern, '')
	if (!allowDecimal) return sanitized

	const [integerPart, ...decimalParts] = sanitized.split('.')
	return decimalParts.length > 0
		? `${integerPart}.${decimalParts.join('')}`
		: integerPart
}

const CattlesFilterContent = (props: CattlesFilterContentProps) => {
	const {
		activeFilters,
		setActiveFilters,
		isFilterActive,
		returnInitialProps,
		availableStats,
		groupId,
		tagIdSearch,
		identified,
		showLastMilking,
		showFatness,
		showLameness,
		availableGroups,
		resetInline = false,
	} = props

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
	const [openFilter, setOpenFilter] = useState(false)
	const [selectedCase, setSelectedCase] = useState<FilterSectionKey>('groups')
	const [pendingFilters, setPendingFilters] = useState(activeFilters)
	const [cowsCount, setCowsCount] = useState<number>(0)
	const [touchedRanges, setTouchedRanges] = useState<Set<SeededRangeKey>>(() =>
		getTouchedRanges(activeFilters),
	)

	const markTouched = (key: SeededRangeKey) =>
		setTouchedRanges(prev => (prev.has(key) ? prev : new Set(prev).add(key)))

	const unmarkTouched = (key: FilterSectionKey) =>
		setTouchedRanges(prev => {
			if (!prev.has(key as SeededRangeKey)) return prev
			const next = new Set(prev)
			next.delete(key as SeededRangeKey)
			return next
		})

	const sections = useMemo(
		() =>
			[
				{ key: 'groups' as const, title: 'Группа' },
				hasStatsValue(availableStats.lactation) && {
					key: 'lactation' as const,
					title: 'День лактации',
				},
				showLastMilking &&
					hasStatsValue(availableStats.lastMilking) && {
						key: 'lastMilking' as const,
						title: 'Последнее доение',
					},
				showLameness && {
					key: 'lameness' as const,
					title: 'Хромота, балл',
				},
				showFatness &&
					hasStatsValue(availableStats.fatness) && {
						key: 'fatness' as const,
						title: 'Упитанность, балл',
					},
				hasStatsValue(availableStats.age) && {
					key: 'cowAge' as const,
					title: 'Возраст коровы',
				},
			].filter(Boolean) as { key: FilterSectionKey; title: string }[],
		[availableStats, showFatness, showLameness, showLastMilking],
	)

	const effectiveFilters = useMemo(
		() =>
			stripSeededRanges(
				pendingFilters,
				availableStats,
				defaultInitialState,
				touchedRanges,
			),
		[availableStats, pendingFilters, touchedRanges],
	)

	const hasPendingFilters = useMemo(
		() =>
			sections.some(section => getSectionActive(section.key, effectiveFilters)),
		[effectiveFilters, sections],
	)

	useEffect(() => {
		if (
			sections.length > 0 &&
			!sections.some(section => section.key === selectedCase)
		) {
			setSelectedCase(sections[0].key)
		}
	}, [sections, selectedCase])

	useEffect(() => {
		if (!openFilter) return

		CattlesApiService.getCowsCount(
			effectiveFilters,
			groupId,
			tagIdSearch,
			identified,
		).then(res => setCowsCount(res.data))
	}, [effectiveFilters, groupId, identified, openFilter, tagIdSearch])

	const seedRangeDefaults = useCallback(
		(
			key: FilterSectionKey,
			filters: IFiltersLamenessTable,
		): IFiltersLamenessTable => {
			if (key === 'cowAge' && !hasRangeValue(filters.cowAge)) {
				if (!hasStatsValue(availableStats.age)) return filters
				return {
					...filters,
					cowAge: {
						startOf: availableStats.age.start,
						endOf: availableStats.age.end,
					},
				}
			}

			if (key === 'lactation' && !hasRangeValue(filters.lactation)) {
				if (!hasStatsValue(availableStats.lactation)) return filters
				return {
					...filters,
					lactation: {
						...filters.lactation,
						startOf: availableStats.lactation.start,
						endOf: availableStats.lactation.end,
					},
				}
			}

			if (key === 'lameness' && !hasRangeValue(filters.lameness)) {
				if (!hasStatsValue(availableStats.limpnessDays)) return filters
				return {
					...filters,
					lameness: {
						...filters.lameness,
						startOf: availableStats.limpnessDays.start,
						endOf: availableStats.limpnessDays.end,
					},
				}
			}

			if (key === 'fatness' && !hasRangeValue(filters.fatness)) {
				if (!hasStatsValue(availableStats.fatness)) return filters
				return {
					...filters,
					fatness: {
						startOf: availableStats.fatness.start,
						endOf: availableStats.fatness.end,
					},
				}
			}

			return filters
		},
		[availableStats],
	)

	useEffect(() => {
		if (!openFilter) return
		setPendingFilters(prev => seedRangeDefaults(selectedCase, prev))
	}, [openFilter, selectedCase, seedRangeDefaults])

	const handleSelectSection = (key: FilterSectionKey) => {
		setSelectedCase(key)
	}

	const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
		setPendingFilters(activeFilters)
		setTouchedRanges(getTouchedRanges(activeFilters))
		setAnchorEl(event.currentTarget)
		setOpenFilter(prev => !prev)
	}

	const handleClose = () => {
		setOpenFilter(false)
	}

	const handleApply = () => {
		if (!hasPendingFilters) return
		setActiveFilters(effectiveFilters)
		setOpenFilter(false)
	}

	const handleResetAll = () => {
		setPendingFilters(defaultInitialState)
		setTouchedRanges(new Set())
	}

	const handleResetCategory = (key: FilterSectionKey) => {
		unmarkTouched(key)
		setPendingFilters(prev => ({
			...prev,
			[key]: defaultInitialState[key],
		}))
	}

	const updateRange = (
		property: 'cowAge' | 'lactation' | 'lameness',
		key: 'startOf' | 'endOf',
		value: string,
		stats: RangeValue,
		allowDecimal = false,
	) => {
		const normalizedValue = normalizeNumberInput(value, allowDecimal)
		markTouched(property)
		setPendingFilters(prev => ({
			...prev,
			[property]: {
				...prev[property],
				[key]: normalizedValue,
				...(normalizedValue !== '' && key === 'startOf'
					? {
							endOf: isEmptyValue(prev[property].endOf)
								? stats.end
								: prev[property].endOf,
						}
					: {}),
				...(normalizedValue !== '' && key === 'endOf'
					? {
							startOf: isEmptyValue(prev[property].startOf)
								? stats.start
								: prev[property].startOf,
						}
					: {}),
			},
		}))
	}

	const updateFatness = (key: 'startOf' | 'endOf', value: number) => {
		markTouched('fatness')
		setPendingFilters(prev => {
			const nextFatness = { ...prev.fatness, [key]: value }
			const start = Number(nextFatness.startOf)
			const end = Number(nextFatness.endOf)

			if (
				nextFatness.startOf !== '' &&
				nextFatness.endOf !== '' &&
				start > end
			) {
				return {
					...prev,
					fatness: {
						startOf: end,
						endOf: start,
					},
				}
			}

			return {
				...prev,
				fatness: nextFatness,
			}
		})
	}

	const toggleLameness = (status: LamenessStatus) => {
		setPendingFilters(prev => ({
			...prev,
			lameness: {
				...prev.lameness,
				statuses: prev.lameness.statuses.includes(status)
					? prev.lameness.statuses.filter(item => item !== status)
					: [...prev.lameness.statuses, status],
			},
		}))
	}

	const renderRangeInputs = (
		property: 'cowAge' | 'lactation' | 'lameness',
		stats: RangeValue,
		allowDecimal = false,
		placeholder = 'Дни',
	) => (
		<Box display='flex' flexDirection='row' alignItems='center' gap='8px'>
			<OutlinedInput
				label='none'
				notched={false}
				type='text'
				placeholder={stats.start !== '' ? `${stats.start}` : placeholder}
				value={
					isEmptyValue(pendingFilters[property].startOf)
						? ''
						: pendingFilters[property].startOf
				}
				startAdornment={
					<InputAdornment position='start'>
						<TypographySecondary>от</TypographySecondary>
					</InputAdornment>
				}
				onChange={event =>
					updateRange(
						property,
						'startOf',
						event.target.value,
						stats,
						allowDecimal,
					)
				}
				sx={rangeInputSx}
			/>
			<TypographySecondary>-</TypographySecondary>
			<OutlinedInput
				label='none'
				notched={false}
				type='text'
				placeholder={stats.end !== '' ? `${stats.end}` : placeholder}
				value={
					isEmptyValue(pendingFilters[property].endOf)
						? ''
						: pendingFilters[property].endOf
				}
				startAdornment={
					<InputAdornment position='start'>
						<TypographySecondary>до</TypographySecondary>
					</InputAdornment>
				}
				onChange={event =>
					updateRange(
						property,
						'endOf',
						event.target.value,
						stats,
						allowDecimal,
					)
				}
				sx={rangeInputSx}
			/>
		</Box>
	)

	const renderContent = () => {
		switch (selectedCase) {
			case 'cowAge':
				return renderRangeInputs('cowAge', availableStats.age, true, 'Год')
			case 'lactation':
				return renderRangeInputs('lactation', availableStats.lactation)
			case 'lastMilking':
				return (
					<DateRangePicker
						startDate={
							pendingFilters.lastMilking.startOf ||
							availableStats.lastMilking.start
						}
						endDate={
							pendingFilters.lastMilking.endOf || availableStats.lastMilking.end
						}
						onChangeUpdateDate={value =>
							setPendingFilters(prev => ({
								...prev,
								lastMilking: {
									startOf: value[0],
									endOf: value[1],
								},
							}))
						}
						restrictionType={DateRestriction.PAST}
					/>
				)
			case 'groups':
				return (
					<AutocompleteInput
						widthForInput='100%'
						onClick={id => {
							setPendingFilters(prev => {
								const ids = prev.groups.ids
								const updatedIds = ids.includes(id)
									? ids.filter(item => item !== id)
									: [...ids, id]

								return {
									...prev,
									groups: {
										ids: updatedIds,
									},
								}
							})
						}}
						isCheckbox
						availableList={availableGroups}
						selectedValues={pendingFilters.groups.ids}
						label='Группа'
						limitTags={2}
						needChip
						startIcon={<SearchIcon stroke={COLORS.icon} />}
						isShowTags={false}
					/>
				)
			case 'fatness':
				return (
					<Box display='flex' flexDirection='row' alignItems='center' gap='8px'>
						<SelectSmall
							typeArray={fatnessOptions}
							isCheckbox={false}
							width='120px'
							isMultiplaySelect={false}
							value={
								isEmptyValue(pendingFilters.fatness.startOf)
									? ''
									: pendingFilters.fatness.startOf
							}
							height='54px'
							placeholder={`от ${availableStats.fatness.start}`}
							startAdornmentDesc='от'
							scrollbarColor='#7F7F84'
							onClick={type => updateFatness('startOf', type)}
						/>
						<TypographySecondary>-</TypographySecondary>
						<SelectSmall
							typeArray={fatnessOptions}
							isCheckbox={false}
							width='120px'
							isMultiplaySelect={false}
							value={
								isEmptyValue(pendingFilters.fatness.endOf)
									? ''
									: pendingFilters.fatness.endOf
							}
							height='54px'
							placeholder={`до ${availableStats.fatness.end}`}
							startAdornmentDesc='до'
							scrollbarColor='#7F7F84'
							onClick={type => updateFatness('endOf', type)}
						/>
					</Box>
				)
			case 'lameness':
				return (
					<Box>
						<ul>
							{lamenessStatuses.map(status => (
								<li
									key={status.id}
									onClick={() => toggleLameness(status.key)}
									style={{
										display: 'flex',
										flexDirection: 'row',
										alignItems: 'center',
										padding: '6px 0px',
										cursor: 'pointer',
									}}
								>
									<Checkbox
										icon={icon}
										disableRipple
										checkedIcon={checkedIcon}
										style={{ padding: '0px 8px 0px 0px', margin: '0px' }}
										checked={pendingFilters.lameness.statuses.includes(
											status.key,
										)}
									/>
									<TypographyPrimary>{status.title}</TypographyPrimary>
								</li>
							))}
						</ul>
						<TypographyPrimary sx={{ margin: '16px 0px' }}>
							Продолжительность, дни
						</TypographyPrimary>
						{renderRangeInputs('lameness', availableStats.limpnessDays)}
					</Box>
				)
			default:
				return null
		}
	}

	return (
		<>
			<Box
				sx={{
					position: 'relative',
					...(resetInline
						? { display: 'flex', alignItems: 'center', gap: '12px' }
						: {}),
				}}
			>
				{isFilterActive && (
					<TypographySecondary
						onClick={returnInitialProps}
						sx={{
							whiteSpace: 'nowrap',
							cursor: 'pointer',
							...(resetInline
								? {}
								: {
										position: 'absolute',
										top: '10px',
										left: '-96px',
									}),
						}}
					>
						Сбросить всё
					</TypographySecondary>
				)}
				<CustomButton
					title='Фильтр'
					onClick={handleOpen}
					iconElement={
						<Box sx={{ position: 'relative' }}>
							<FilterIcon />
							{isFilterActive && (
								<Box
									component='span'
									sx={{
										position: 'absolute',
										width: '10px',
										height: '10px',
										borderRadius: '50%',
										backgroundColor: COLORS.health,
										bottom: '7px',
										right: '-4px',
									}}
								/>
							)}
						</Box>
					}
					colors={[COLORS.main, '#fff']}
				/>
			</Box>
			<Popover
				open={openFilter}
				onClose={handleClose}
				anchorEl={anchorEl}
				transitionDuration={200}
				anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
				transformOrigin={{ horizontal: 'right', vertical: -8 }}
				sx={{
					'.MuiPopover-paper': {
						width: '47vw',
						boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.15)',
						padding: '16px 24px',
						borderRadius: '8px',
					},
				}}
			>
				<Box
					display='flex'
					flexDirection='row'
					justifyContent='space-between'
					alignItems='center'
				>
					<TypographyPrimary16Bold>Фильтр</TypographyPrimary16Bold>
					<CrossCloseLogo
						style={{ cursor: 'pointer', stroke: COLORS.icon }}
						onClick={handleClose}
					/>
				</Box>
				<Box
					display='flex'
					flexDirection='row'
					height='387px'
					width='100%'
					marginTop='24px'
				>
					<ul
						style={{
							listStyleType: 'none',
							minWidth: '180px',
							borderRight: `1px solid ${COLORS.inputBorder}`,
						}}
					>
						{sections.map(section => (
							<li
								key={section.key}
								style={{
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'center',
									justifyContent: 'space-between',
									padding: '7px 0px',
									flexWrap: 'wrap',
								}}
							>
								<TypographyPrimary
									onClick={() => handleSelectSection(section.key)}
									sx={{
										color:
											selectedCase === section.key ? COLORS.main : undefined,
										position: 'relative',
										cursor: 'pointer',
									}}
								>
									{section.title}
								</TypographyPrimary>

								{getSectionActive(section.key, effectiveFilters) && (
									<CrossCloseLogo
										style={{
											cursor: 'pointer',
											width: '16px',
											height: '12px',
											marginRight: '8px',
										}}
										stroke={COLORS.secondary}
										onClick={() => handleResetCategory(section.key)}
									/>
								)}
							</li>
						))}
					</ul>
					<Box sx={{ marginLeft: '24px', width: '75%' }}>
						<TypographyHeader14 sx={{ marginBottom: '16px' }}>
							{sections.find(section => section.key === selectedCase)?.title}
						</TypographyHeader14>
						{renderContent()}
					</Box>
				</Box>
				<Box
					display='flex'
					flexDirection='column'
					alignItems='flex-end'
					justifyContent='flex-end'
				>
					<TypographySecondary>Результат: {cowsCount}</TypographySecondary>
					<Box
						display='flex'
						flexDirection='row'
						alignItems='center'
						justifyContent='flex-end'
						width='100%'
						mt='16px'
						gap='16px'
					>
						{hasPendingFilters && (
							<TypographySecondary
								onClick={handleResetAll}
								sx={{ whiteSpace: 'nowrap', cursor: 'pointer' }}
							>
								Сбросить
							</TypographySecondary>
						)}
						<ButtonUI
							disabled={!hasPendingFilters}
							sx={{
								marginTop: '0px',
								width: '400px',
								height: '56px',
								fontWeight: 600,
								fontSize: '14px',
								lineHeight: '17px',
							}}
							onClick={handleApply}
						>
							Применить
						</ButtonUI>
					</Box>
				</Box>
			</Popover>
		</>
	)
}

export default CattlesFilterContent
